// Shared UI primitives — status pills, skeletons, empty states, KPI stat.
// Status never relies on color alone (icon + label), per the a11y guideline.
import type { ReactNode, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, AlertTriangle, XCircle, Circle, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';

type Tone = 'ok' | 'warning' | 'critical' | 'muted' | 'info';

const TONE: Record<Tone, { text: string; bg: string; ring: string }> = {
  ok: { text: 'text-ok', bg: 'bg-ok/10', ring: 'ring-ok/30' },
  warning: { text: 'text-warning', bg: 'bg-warning/10', ring: 'ring-warning/30' },
  critical: { text: 'text-critical', bg: 'bg-critical/10', ring: 'ring-critical/30' },
  muted: { text: 'text-muted', bg: 'bg-white/5', ring: 'ring-white/10' },
  info: { text: 'text-primary', bg: 'bg-primary/10', ring: 'ring-primary/30' },
};

const STATUS_ICON: Record<string, typeof Circle> = {
  online: CheckCircle2, active: CheckCircle2, ok: CheckCircle2, resolved: CheckCircle2,
  warning: AlertTriangle, pending_renewal: AlertTriangle, 'under-review': AlertTriangle, on_hold: AlertTriangle, in_progress: AlertTriangle,
  offline: XCircle, critical: XCircle, terminated: XCircle, closed: Circle,
};

/** Map a domain status/severity to a tone. */
export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (['online', 'active', 'ok', 'resolved'].includes(s)) return 'ok';
  if (['warning', 'pending_renewal', 'under-review', 'on_hold', 'in_progress', 'medium'].includes(s)) return 'warning';
  if (['offline', 'critical', 'terminated', 'high'].includes(s)) return 'critical';
  return 'muted';
}

export function StatusPill({ status, label }: { status: string; label?: string }) {
  const tone = statusTone(status);
  const t = TONE[tone];
  const Icon = STATUS_ICON[status.toLowerCase()] ?? Circle;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${t.text} ${t.bg} ${t.ring}`}>
      <Icon size={12} />
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}

export function PriorityPill({ priority }: { priority: string }) {
  const tone: Tone = priority === 'critical' || priority === 'high' ? 'critical'
    : priority === 'medium' ? 'warning' : 'muted';
  const t = TONE[tone];
  const label = priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : priority;
  return <span className={`inline-flex justify-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${t.text} ${t.bg} ${t.ring}`}>{label}</span>;
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-white/10 ${className}`} />;
}

export function EmptyState({ icon: Icon, title, hint, action }: {
  icon: typeof Circle; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="grid place-items-center gap-2 py-10 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-white/5 text-muted"><Icon size={22} /></div>
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="max-w-xs text-xs text-muted">{hint}</div>}
      {action}
    </div>
  );
}

export function KpiStat({ label, value, delta, tone = 'info', unit, onClick, hint }: {
  label: string; value: string | number; delta?: number; tone?: Tone; unit?: string;
  /** When set, the whole tile becomes a button that drills into the detail. */
  onClick?: () => void;
  /** Native tooltip explaining what the metric means (shown on the label). */
  hint?: string;
}) {
  const { t: translate } = useTranslation('components');
  const t = TONE[tone];
  const DeltaIcon = delta === undefined ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaTone = delta === undefined || delta === 0 ? 'text-muted' : delta > 0 ? 'text-ok' : 'text-critical';
  const clickable = !!onClick;
  return (
    <div
      className={`tk-card tk-card-hover ${clickable ? 'cursor-pointer text-start transition-shadow hover:ring-1 hover:ring-white/20' : ''}`}
      {...(clickable ? { role: 'button', tabIndex: 0, onClick, onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); } } } : {})}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted" title={hint}>{label}</span>
        <span className={`grid h-7 w-7 place-items-center rounded-md ${t.bg} ${t.text}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="tk-num text-3xl font-semibold">{value}</span>
        {unit && <span className="text-sm text-muted">{unit}</span>}
      </div>
      {delta !== undefined && (
        <div className={`mt-1 flex items-center gap-1 text-xs ${deltaTone}`}>
          <DeltaIcon size={13} /> {delta > 0 ? '+' : ''}{delta} {translate('primitives.kpi.vs24h')}
        </div>
      )}
    </div>
  );
}
